import React, { useState, useEffect } from "react";
import { apiJson, api, apiBlob } from "../../utils/api";

const MeritList = () => {
  const [sessions, setSessions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedDegree, setSelectedDegree] = useState("");
  const [search, setSearch] = useState("");
  const [file, setFile] = useState(null);
  const [uploadSession, setUploadSession] = useState("");
  const [uploadDegree, setUploadDegree] = useState("");
  const [uploadProcess, setUploadProcess] = useState("");
  const [processes, setProcesses] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    apiJson("/api/admin/master/academic-sessions").then((data) => {
      setSessions(data);
      const active = data.find((s) => s.isActive);
      if (active) setSelectedSession(String(active.id));
    }).catch(() => {});
    apiJson("/api/admin/master/degrees").then(setDegrees).catch(() => {});
    apiJson("/api/admin/master/admission-processes").then(setProcesses).catch(() => {});
  }, []);

  // Reset & filter processes when session/degree change
  const matchingProcesses = processes.filter(
    (p) =>
      (!uploadSession || p.academicSessionId === parseInt(uploadSession)) &&
      (!uploadDegree || p.degreeId === parseInt(uploadDegree))
  );

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSession) params.set("academicSessionId", selectedSession);
      if (selectedDegree) params.set("degreeId", selectedDegree);
      if (search) params.set("search", search);
      const data = await apiJson(`/api/admin/merit-list?${params}`);
      setEntries(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [selectedSession, selectedDegree]);

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (!file || !uploadSession || !uploadDegree || !uploadProcess) {
      setError("Please select file, session, degree, and admission process");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("academicSessionId", uploadSession);
    formData.append("degreeId", uploadDegree);
    formData.append("admissionProcessId", uploadProcess);

    try {
      const res = await api("/api/admin/merit-list/upload", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setMessage(data.message);
      setFile(null);
      fetchEntries();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSession) params.set("academicSessionId", selectedSession);
      if (selectedDegree) params.set("degreeId", selectedDegree);
      const blob = await apiBlob(`/api/admin/merit-list/download?${params}`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `merit_list_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await apiJson(`/api/admin/merit-list/${id}`, { method: "DELETE" });
      fetchEntries();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-4">Merit List</h1>

      {/* Upload Section */}
      <form onSubmit={handleUpload} className="bg-white p-4 rounded shadow mb-4">
        <h3 className="font-semibold text-gray-700 mb-3">Upload Merit List</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <select
            value={uploadSession}
            onChange={(e) => { setUploadSession(e.target.value); setUploadProcess(""); }}
            className="px-2 py-1.5 border rounded text-sm"
            required
          >
            <option value="">Select Session</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <select
            value={uploadDegree}
            onChange={(e) => { setUploadDegree(e.target.value); setUploadProcess(""); }}
            className="px-2 py-1.5 border rounded text-sm"
            required
          >
            <option value="">Select Degree</option>
            {degrees.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <select
            value={uploadProcess}
            onChange={(e) => setUploadProcess(e.target.value)}
            className="px-2 py-1.5 border rounded text-sm"
            required
          >
            <option value="">Select Admission Process</option>
            {matchingProcesses.map((p) => (
              <option key={p.id} value={p.id}>
                Round {p.roundNumber}{p.isSpotAdmission ? " (Spot)" : ""} — {new Date(p.fromDate).toLocaleDateString("en-IN")} to {new Date(p.toDate).toLocaleDateString("en-IN")}
              </option>
            ))}
          </select>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => setFile(e.target.files[0])}
            className="text-sm"
          />
          <button
            type="submit"
            className="bg-indigo-600 text-white px-4 py-1.5 rounded text-sm hover:bg-indigo-700"
          >
            Upload
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Expected columns: Registration Number, Student Name, Date of Birth, Merit Rank (optional)
        </p>
      </form>

      {message && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 text-sm">{message}</div>}
      {error && <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">{error}</div>}

      {/* Filter Section */}
      <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-3">
        <select
          value={selectedSession}
          onChange={(e) => setSelectedSession(e.target.value)}
          className="px-2 py-1.5 border rounded text-sm"
        >
          <option value="">Filter by Session</option>
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select
          value={selectedDegree}
          onChange={(e) => setSelectedDegree(e.target.value)}
          className="px-2 py-1.5 border rounded text-sm"
        >
          <option value="">Filter by Degree</option>
          {degrees.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-2 py-1.5 border rounded text-sm"
        />
        <button
          onClick={fetchEntries}
          className="bg-gray-200 text-gray-700 px-4 py-1.5 rounded text-sm hover:bg-gray-300"
        >
          Search
        </button>
        <button
          onClick={handleDownload}
          className="bg-emerald-600 text-white px-4 py-1.5 rounded text-sm hover:bg-emerald-700"
        >
          Download Excel
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-3 py-2">Rank</th>
              <th className="text-left px-3 py-2">Reg No</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">DOB</th>
              <th className="text-left px-3 py-2">Degree</th>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Admitted</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-gray-400">
                  No entries found
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2">{e.meritRank ?? "-"}</td>
                  <td className="px-3 py-2">{e.applyId}</td>
                  <td className="px-3 py-2">{e.studentName}</td>
                  <td className="px-3 py-2">{new Date(e.dateOfBirth).toLocaleDateString("en-IN")}</td>
                  <td className="px-3 py-2">{e.degree?.name}</td>
                  <td className="px-3 py-2">{e.academicSession?.name}</td>
                  <td className="px-3 py-2">{e.isAdmitted ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => handleDelete(e.id)} className="text-red-600 hover:underline text-xs">
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MeritList;
